export const simulatorDropPressure = ({
  pip,
  flowRegime,
  correctedQt,
  correctedQg,
  ap,
  correctedLiquidDensity,
  correctedGasDensity,
  correctedQl,
  IDdiptube,
  vT,
  liquidViscosity,
  dipTubeLength,
  correctedFlowRateT,
  avgPressure,
  reynoldsL,
  flowRegimeLM,
  flowRegimeLS,
  flowRegimeVgD,
}: {
  pip: number;
  flowRegime: "Slug" | "Bubble" | "Mist" | "Transition";
  correctedQt: number;
  correctedQg: number;
  ap: number;
  correctedLiquidDensity: number;
  correctedGasDensity: number;
  correctedQl: number;
  IDdiptube: number;
  vT: number;
  liquidViscosity: number;
  dipTubeLength: number;
  correctedFlowRateT: number;
  avgPressure: number;
  reynoldsL: number;
  flowRegimeLM: number;
  flowRegimeLS: number;
  flowRegimeVgD: number;
}) => {
  console.log("pip", pip);
  console.log("flowRegime", flowRegime);
  console.log("correctedQt", correctedQt);
  console.log("correctedQg", correctedQg);
  console.log("ap", ap);
  console.log("correctedLiquidDensity", correctedLiquidDensity);
  console.log("correctedGasDensity", correctedGasDensity);
  console.log("correctedQl", correctedQl);
  console.log("IDdiptube", IDdiptube);
  console.log("vT", vT);
  console.log("liquidViscosity", liquidViscosity);
  console.log("dipTubeLength", dipTubeLength);
  console.log("correctedFlowRateT", correctedFlowRateT);
  console.log("avgPressure", avgPressure);
  console.log("reynoldsL", reynoldsL);
  console.log("flowRegimeLM", flowRegimeLM);
  console.log("flowRegimeLS", flowRegimeLS);
  console.log("flowRegimeVgD", flowRegimeVgD);
  const frictionFactor = 0.029;
  const vB = 1.75;
  const IDFeet = IDdiptube / 12;
  const g = 32.17;

  const calculateNB = () =>
    (1488 * vB * IDFeet * correctedLiquidDensity) / liquidViscosity;

  const calculateVBb = (nB: number) => {
    if (nB < 3000) {
      return (0.546 + 8.74e-6 * reynoldsL) * Math.sqrt(g * IDFeet);
    } else if (nB > 8000) {
      return (0.35 + 8.74e-6 * reynoldsL) * Math.sqrt(g * IDFeet);
    } else {
      const term1 = (0.251 + 8.74e-6 * reynoldsL) * Math.sqrt(g * IDFeet);
      const term2 = Math.sqrt(
        term1 ** 2 +
          (13.59 * liquidViscosity) /
            (correctedLiquidDensity * Math.sqrt(IDFeet)),
      );
      return 0.5 * (term1 + term2);
    }
  };

  const calculateLiquidDis = () =>
    vT < 10
      ? (0.0127 * Math.log10(liquidViscosity + 1)) / IDFeet ** 1.415 -
        0.284 +
        0.167 * Math.log10(vT) +
        0.113 * Math.log10(IDFeet)
      : (0.0274 * Math.log10(liquidViscosity + 1)) / IDFeet ** 1.371 +
        0.161 +
        0.569 * Math.log10(IDFeet) -
        Math.log10(vT) *
          ((0.01 * Math.log10(liquidViscosity + 1)) / IDFeet ** 1.571 +
            0.397 +
            0.63 * Math.log10(IDFeet));

  const calculateFrictionLossGrad = (
    density: number,
    velocity: number,
    liquidDis: number,
  ) =>
    ((frictionFactor * density * velocity ** 2) / (2 * g * IDFeet)) * liquidDis;

  const calculateDeltaP = (avgDensity: number, frictionLossGrad: number) => {
    const deltaDd =
      (144 *
        (pip * 0.1) *
        (1 -
          (correctedFlowRateT * correctedQg) /
            (4637 * IDFeet ** 2 * avgPressure))) /
      (avgDensity + frictionLossGrad);
    return (pip * 0.1) / deltaDd;
  };

  const nB = calculateNB();
  const vBb = calculateVBb(nB);
  const liquidDis = calculateLiquidDis();

  if (flowRegime === "Bubble") {
    const fg =
      0.5 *
      (1 +
        correctedQt / (0.8 * ap) -
        Math.sqrt(
          (1 + correctedQt / (0.8 * ap)) ** 2 - (4 * correctedQg) / (0.8 * ap),
        ));
    const avgDensity =
      (1 - fg) * correctedLiquidDensity + fg * correctedGasDensity;
    const vL = correctedQl / (ap * (1 - fg));
    const frictionLossGrad = calculateFrictionLossGrad(
      correctedLiquidDensity,
      vL,
      1,
    );
    const deltaPdeltaD = calculateDeltaP(avgDensity, frictionLossGrad);
    return pip + deltaPdeltaD * dipTubeLength;
  } else if (flowRegime === "Slug") {
    const avgDensity =
      (correctedFlowRateT + correctedLiquidDensity * vBb * ap) /
        (correctedQt + vBb * ap) +
      liquidDis * correctedLiquidDensity;
    const frictionLossGradSlug = calculateFrictionLossGrad(
      correctedLiquidDensity,
      vT,
      liquidDis,
    );
    const deltaPdeltaDd = calculateDeltaP(avgDensity, frictionLossGradSlug);
    return pip + deltaPdeltaDd * dipTubeLength;
  } else if (flowRegime === "Mist") {
    const fG = 1 / (1 + correctedQl / correctedQg);
    const vGg = correctedQg / (ap * fG);
    const avgDensity =
      (1 - fG) * correctedLiquidDensity + fG * correctedGasDensity;
    const frictrionLossGradMist = calculateFrictionLossGrad(
      correctedGasDensity,
      vGg,
      1,
    );
    const deltaPdeltaDd = calculateDeltaP(avgDensity, frictrionLossGradMist);
    return pip + deltaPdeltaDd * dipTubeLength;
  } else {
    const qQ = ap * flowRegimeLM * (correctedLiquidDensity * 0.534) ** (-1 / 4);
    const fgMist = 1 / (1 + correctedQl / qQ);
    const avgDensityMist =
      (1 - fgMist) * correctedLiquidDensity + fgMist * correctedGasDensity;
    const avgDensity2 =
      ((flowRegimeLM - flowRegimeVgD) / (flowRegimeLM - flowRegimeLS)) *
        ((correctedFlowRateT + correctedLiquidDensity * vBb * ap) /
          (correctedQt + vBb * ap) +
          liquidDis * correctedLiquidDensity) +
      ((flowRegimeVgD - flowRegimeLS) / (flowRegimeLM - flowRegimeLS)) *
        avgDensityMist;
    const frictionLossGradTransition =
      ((flowRegimeLM - flowRegimeVgD) / (flowRegimeLM - flowRegimeLS)) *
        calculateFrictionLossGrad(correctedLiquidDensity, vT, liquidDis) +
      ((flowRegimeVgD - flowRegimeLS) / (flowRegimeLM - flowRegimeLS)) *
        calculateFrictionLossGrad(correctedGasDensity, vBb, 1);
    const dDeltapDeltaD = calculateDeltaP(
      avgDensity2,
      frictionLossGradTransition,
    );
    return pip + dipTubeLength * dDeltapDeltaD;
  }
};
